import { auth } from "../utils/firebaseAuth";
import { Image } from "../components/types/ArticleTypes";
import { Article } from "../components/types/ArticleTypes";

class ApiHelper {
    private baseUrl: string;

    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;
    }

    private async getAuthToken(): Promise<string> {
        const user = auth.currentUser;
        if (user) {
            return user.getIdToken();
        } else {
            throw new Error('User is not authenticated');
        }
    }
    private async fetchWithAuth(url: string, options: RequestInit): Promise<Response> {
        const token = await this.getAuthToken();
        const headers = {
            ...options.headers,
            'Authorization': `Bearer ${token}`,
        };
        return fetch(url, { ...options, headers });
    }

    async fetchArticles() {
        const response = await fetch(`${this.baseUrl}/article`);
        if (!response.ok) {
            throw new Error('Failed to fetch articles');
        }
        return response.json();
    }

    async fetchArticle(articleId: string) {
        const response = await fetch(`${this.baseUrl}/article/${articleId}`);
        if (!response.ok) {
            throw new Error('Failed to fetch article');
        }
        return response.json();
    }

    async fetchArticlesByCategory(category: string) {
        const response = await fetch(`${this.baseUrl}/category/${category}`);
        if (!response.ok) {
            throw new Error('Failed to fetch articles by category');
        }
        return response.json();
    }

    async uploadImage(articleId: string, selectedFile: File) {
        const formData = new FormData();
        formData.append('image', selectedFile);
        formData.append('articleId', articleId);

        const response = await this.fetchWithAuth(`${this.baseUrl}/article/upload`, {
            method: 'POST',
            body: formData,
        });

        if (!response.ok) {
            throw new Error('Error uploading image');
        }
        return response.json();
    }

    async updateArticle(article: Article) {
        const response = await this.fetchWithAuth(`${this.baseUrl}/article`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(article),
        });

        if (!response.ok) {
            throw new Error('Failed to update article');
        }
    }

    async deleteArticle(articleId: string) {
        const response = await this.fetchWithAuth(`${this.baseUrl}/article/${articleId}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            }
        });

        if (!response.ok) {
            throw new Error('Failed to delete article');
        }
    }

    async createArticle(article: Article) {
        console.log(JSON.stringify(article));
        const response = await this.fetchWithAuth(`${this.baseUrl}/article`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(article),
        });

        if (!response.ok) {
            throw new Error('Failed to create article');
        }
    }

    async fetchArticleImages(articleId: string) {
        const response = await fetch(`${this.baseUrl}/articleimage/${articleId}`, {
            method: 'GET',
        });
        if (response.status === 404) {
            return { images: [] };
        }
        if (!response.ok) {
            throw new Error('Failed to fetch article images');
        }
        return response.json();
    }

    async updateArticleImageData(articleId: string, imageData: Image) {
        const response = await this.fetchWithAuth(`${this.baseUrl}/articleimage/update?articleId=${articleId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(imageData),
        });

        if (!response.ok) {
            throw new Error('Failed to update imageData');
        }
    }

    async addArticleImage(articleId: string, imageFile: File, imageData: Image) {
        const formData = new FormData();
        formData.append('image', imageFile);
        formData.append('articleId', articleId);
        formData.append('type', imageData.type);
        formData.append('title', imageData.title);
        formData.append('content', imageData.content);
        formData.append('source', imageData.source);
        formData.append('credit', imageData.credit);

        const response = await this.fetchWithAuth(`${this.baseUrl}/articleimage/upload`, {
            method: 'POST',
            body: formData,
        });

        if (!response.ok) {
            throw new Error('Failed to add image');
        }
    }

    async editArticleImage(imageId: string, imageFile: File) {
        const formData = new FormData();
        formData.append('image', imageFile);

        const response = await this.fetchWithAuth(`${this.baseUrl}/articleimage/${imageId}`, {
            method: 'PUT',
            body: formData,
        });

        if (!response.ok) {
            throw new Error('Failed to edit image');
        }
        return response.json();
    }

    async deleteArticleImage(articleId: string, imageUrl: string) {
        const encodedImageFile = encodeURIComponent(imageUrl);
        const response = await this.fetchWithAuth(`${this.baseUrl}/articleimage/${articleId}/${encodedImageFile}`, {
            method: 'DELETE',
        });

        if (!response.ok) {
            throw new Error('Failed to delete image');
        }
    }
}

const getApiUrl = () => {
    const env = process.env.REACT_APP_ENV || 'local';
    console.log(`Environment: ${env}`);

    switch (env) {
        case 'production':
            return 'https://articleapi.myappnetwork.com/v0/api';
        case 'staging':
            return 'https://staging-articleapi.myappnetwork.com/v0/api';
        default:
            return 'http://localhost:5169/v0/api';
    }
};

const apiHelper = new ApiHelper(getApiUrl());

export default apiHelper;